import { createListenerMiddleware } from '@reduxjs/toolkit';
import history from '../history';
import { gradeBenchPanelActions, mergeCasesActions, navigationIntentActions } from '@codametrix/ui-common';
import { navigateContext } from '../actions/navigation.actions';
import { getGlobalNavInstrumentation } from '../core/nav-intrumentation.context';
import { loggerFactory } from '@codametrix/client-logging';
const { navigationIntent } = navigationIntentActions;
const { launchInAmplify } = mergeCasesActions;
const logger = loggerFactory('NavMiddleware');

const NAVIGATION_ROUTES: Record<string, string> = {
  NOT_AUTHORIZED: '/not-authorized/',
  CASE_NOT_FOUND: '/case-not-found/',
  CASE_CHOOSER: '/case-chooser/',
  EULA: '/eula/',
  SESSION_OPENED: '/session-opened/',
  CONTEXT_UNAVAILABLE: '/context-unavailable/',
  TIMED_OUT: '/timed-out/',
};

const listenerMiddleware = createListenerMiddleware();

const getNavService = () => {
  const service = getGlobalNavInstrumentation();
  if (!service) {
    logger.warn('Nav-Instrumentation service not intialized');
  }
  return service;
};

listenerMiddleware.startListening({
  predicate: (action): action is ReturnType<typeof navigationIntent> =>
    action.type === navigationIntent.type,
  effect: (action) => {
    const url = action.payload.url ?? NAVIGATION_ROUTES[action.payload.intent];
    
    if (url) {
      const service = getNavService();
      if (service) {
        service.trackItemActivated(
          { id: action.payload.intent, label: `Route: ${action.payload.intent}` },
          'SYSTEM_INTENT'
        );
      }

      if (action.payload.replace) {
        history.replace(url, action.payload.state);
      } else {
        history.push(url, action.payload.state);
      }
    }
  }
});

listenerMiddleware.startListening({
  predicate: (action): boolean =>
    action.type === launchInAmplify.pending.type,
  effect: (action) => {
    // RTK pending actions carry the thunk argument in meta.arg, not in payload
    // (payload is always undefined for pending actions).
    const {
      serviceLine,
      caseId,
      processId,
    } = (action as any).meta.arg as CMxCommonApp.LaunchWorkItemConfig;

    const service = getNavService();
    if (service) {
      service.trackItemActivated(
        { 
          id: `case_${caseId}`, 
          label: `Worklist Case Run`, 
          observationService: serviceLine, 
          resourceId: processId 
        },
        'AMPLIFY_LAUNCHER'
      );
    }
    // Navigation is handled by the thunk itself via dispatching navigationIntent,
    // which is picked up by the navigationIntent listener above.
  }
});
listenerMiddleware.startListening({
  actionCreator: navigateContext.fulfilled,
  effect: (action) => {
    if (!action.payload) return;

    const { item } = action.payload;
    const service = getNavService();
    if (service) {
      service.trackItemActivated(item, 'NAV_OPTIONS');
    }
  }
});

// Auto-run resolver on case load: once fetchAllAttributes.fulfilled fires, all rubric attribute
// data (case, code, and span level) is in the state. This ensures the Final Code Set populates
// automatically on graded workitems without requiring a manual answer change.
listenerMiddleware.startListening({
  predicate: (action) =>
    action.type === gradeBenchPanelActions.fetchAllAttributes.fulfilled.type,
  effect: (_action, listenerApi) => {
    listenerApi.dispatch(gradeBenchPanelActions.resolveCQAWorkitem() as any);
  }
});

// Debounced resolver re-run: fires ~800ms after any grading change that affects code membership.
// cancelActiveListeners() ensures rapid edits collapse into a single call.
const RESOLVER_DEBOUNCE_MS = 800;
const GRADING_CHANGE_TYPES = new Set([
  gradeBenchPanelActions.updateAttribute.fulfilled.type,
  gradeBenchPanelActions.deleteAttribute.fulfilled.type,
  gradeBenchPanelActions.addCode.fulfilled.type,
  gradeBenchPanelActions.deleteCQACode.fulfilled.type,
  gradeBenchPanelActions.attachCodeToSpan.fulfilled.type,
  gradeBenchPanelActions.detachCodeFromSpan.fulfilled.type,
]);

listenerMiddleware.startListening({
  predicate: (action) => GRADING_CHANGE_TYPES.has(action.type),
  effect: async (_action, listenerApi) => {
    listenerApi.cancelActiveListeners();
    await listenerApi.delay(RESOLVER_DEBOUNCE_MS);
    listenerApi.dispatch(gradeBenchPanelActions.resolveCQAWorkitem() as any);
  }
});

export default listenerMiddleware;
